import {
  type ColumnDef,
  type ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type SortingState,
  sortingFns,
  useReactTable,
  type VisibilityState,
} from '@tanstack/react-table';
import {
  ChevronDown,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronsLeftIcon,
  ChevronsRightIcon,
  MoreHorizontal,
  Search,
} from 'lucide-react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import * as React from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import type { MatterAccess } from '@/types';
import { useShellHeader, useShellHeaderBelow } from '@/components/contexts/ShellHeaderContext';
import { JoinMatterButton } from '@/components/matter';
import { CreateMatterDialog } from '@/components/matter/CreateMatterDialog';
import { MatterMemberAvatars } from '@/components/shared';
import Loading from '@/components/shared/Loading';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { HeaderTabs } from '@/components/ui/HeaderTabs';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import useAllMatters from '@/hooks/useAllMatters';
import { useCanCreateMatter } from '@/hooks/useCanCreateMatter';
import useMatters from '@/hooks/useMatters';
import { apiErrorMessage, backendClient } from '@/lib/api-client';
import { useAuth } from '@/lib/auth/hooks/auth-provider';
import type { AllMattersTab } from '@/src/router/search-schemas';
import { DeadlinesPanel } from './DeadlinesPanel';
import { ResumePanel } from './ResumePanel';
import { UploadsPanel } from './UploadsPanel';

const ALL_MATTERS_TABS: { value: AllMattersTab; label: string }[] = [
  { value: 'matters', label: 'Matters' },
  { value: 'deadlines', label: 'Deadlines' },
  { value: 'conversations', label: 'Conversations' },
  { value: 'uploads', label: 'Uploads' },
];

export default function Home() {
  const { t } = useTranslation('common');
  const { user: sessionUser } = useAuth();
  const user = sessionUser?.dbUser;
  const navigate = useNavigate();
  const search = useSearch({ from: '/all-matters' });
  const activeTab: AllMattersTab = search.tab ?? 'matters';
  const firstName = user?.first_name;
  const headerOverride = React.useMemo(
    () => ({
      header: firstName ? `Welcome back, ${firstName}` : 'Welcome back',
      subheader: 'View and manage your cases',
    }),
    [firstName],
  );

  useShellHeader(headerOverride);

  const allMattersHeaderBelow = React.useMemo(
    () => (
      <HeaderTabs
        idBase="all-matters"
        ariaLabel="All matters views"
        items={ALL_MATTERS_TABS}
        value={activeTab}
        onChange={(value) => {
          void navigate({
            to: '/all-matters',
            search: {
              ...search,
              tab: value as AllMattersTab,
            },
          });
        }}
      />
    ),
    [activeTab, navigate, search],
  );

  useShellHeaderBelow(allMattersHeaderBelow);

  const viewArchivedMatters = search.archived === true;
  const viewAllMatters = search.viewAll === true;
  const showMattersTab = activeTab === 'matters';

  const handleRowMatterClick = (guid: string) => {
    navigate({
      to: '/matter/$slug/case-files/correspondence',
      params: { slug: guid },
    });
  };

  // Archived is a matters-tab view. Other tabs keep active matters so deadline
  // week-start follows those matters rather than the newest archived one.
  const {
    matters: userMatters,
    isLoading: isLoadingUserMatters,
    isError: isErrorUserMatters,
    mutateMatters,
  } = useMatters(showMattersTab && viewArchivedMatters);

  // Only fetch the firm-wide list when that view is selected on the matters tab.
  const {
    matters: allMatters,
    isLoading: isLoadingAllMatters,
    isError: isErrorAllMatters,
    mutateAllMatters,
  } = useAllMatters(viewAllMatters && showMattersTab);

  const matters = viewAllMatters ? allMatters : userMatters;
  const isLoading = viewAllMatters ? isLoadingAllMatters : isLoadingUserMatters;
  const isError = viewAllMatters ? isErrorAllMatters : isErrorUserMatters;
  const retryLoad = viewAllMatters ? mutateAllMatters : mutateMatters;

  const matterRegion = React.useMemo(() => {
    if (!userMatters.length) return null;
    return (
      [...userMatters].sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      )[0]?.region ?? null
    );
  }, [userMatters]);

  const [sorting, setSorting] = React.useState<SortingState>([
    {
      id: 'created_at',
      desc: true,
    },
  ]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  );

  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({
      created_at: false,
      actions: true,
    });

  React.useEffect(() => {
    setColumnVisibility((prev) => ({
      ...prev,
      actions: !viewAllMatters,
    }));
  }, [viewAllMatters]);

  const [rowSelection, setRowSelection] = React.useState({});
  const [globalFilter, setGlobalFilter] = React.useState('');
  const [createMatterDialogOpen, setCreateMatterDialogOpen] =
    React.useState(false);
  const canCreateMatter = useCanCreateMatter();

  const columns: ColumnDef<MatterAccess>[] = React.useMemo(
    () => [
      {
        accessorKey: 'created_at',
        header: () => {
          return <span>Created</span>;
        },
        cell: () => {
          return null;
        },
        enableHiding: true,
        size: 0,
      },
      {
        accessorKey: 'matter_no',
        header: () => {
          return <span>Number</span>;
        },
        cell: ({ row }) => <div>{row.getValue('matter_no')}</div>,
        size: 100,
        meta: {
          headerClassName: 'w-[180px]',
          cellClassName: 'w-[180px]',
        },
      },
      {
        accessorKey: 'name',
        header: () => {
          return <span>Name</span>;
        },
        cell: ({ row }) => <div>{row.getValue('name')}</div>,
        size: 150,
        meta: {
          headerClassName: 'w-[300px]',
          cellClassName: 'w-[300px]',
        },
      },
      {
        accessorKey: 'forum',
        header: () => {
          return <span>Type</span>;
        },
        cell: ({ row }) => {
          const forum = row.getValue('forum') as string | null;
          return <div>{forum || '-'}</div>;
        },
        size: 100,
        meta: {
          headerClassName: 'w-[140px] md:w-[180px]',
          cellClassName: 'w-[140px] md:w-[180px]',
        },
      },
      {
        accessorKey: 'venue',
        header: () => {
          return <span>Venue</span>;
        },
        cell: ({ row }) => <div>{row.getValue('venue')}</div>,
        size: 150,
        meta: {
          headerClassName: 'w-[300px]',
          cellClassName: 'w-[300px]',
        },
      },
      ...(viewAllMatters
        ? [
            {
              id: 'team',
              header: () => {
                return <span>Team</span>;
              },
              cell: ({ row }: { row: any }) => {
                return (
                  <MatterMemberAvatars members={row.original.members ?? []} />
                );
              },
              size: 80,
              meta: {
                headerClassName: 'w-[120px]',
                cellClassName: 'w-[120px]',
              },
            },
          ]
        : []),
      ...(viewAllMatters
        ? [
            {
              id: 'join',
              header: () => {
                return <span>Membership</span>;
              },
              cell: ({ row }: { row: any }) => {
                return (
                  <JoinMatterButton
                    matterAccess={row.original}
                    onSuccess={() => mutateAllMatters()}
                  />
                );
              },
              size: 50,
              meta: {
                headerClassName: 'w-[100px] pr-48',
                cellClassName: 'w-[100px] pr-48',
              },
            },
          ]
        : []),
      {
        id: 'actions',
        enableHiding: true,
        header: () => {
          return <span className="sr-only">Actions</span>;
        },
        cell: ({ row }) => {
          const matter = row.original;

          const toggleMatterStatus = async (e: React.MouseEvent) => {
            e.stopPropagation();
            try {
              const newStatus = !matter.is_active;
              await backendClient.patch(`/matter/update/${matter.guid}`, {
                is_active: newStatus,
              });

              if (viewAllMatters) {
                await mutateAllMatters();
              } else {
                mutateMatters();
              }
            } catch (error) {
              console.error('Failed to update matter status:', error);
              toast.error(
                apiErrorMessage(error) || 'Failed to update matter status',
              );
            }
          };

          const isOwner = matter.current_user_role?.name === 'Owner';

          return (
            <div className="flex justify-end">
              <DropdownMenu>
                <DropdownMenuTrigger
                  asChild
                  onClick={(e) => e.stopPropagation()}
                >
                  <Button variant="ghost" className="h-8 w-8 p-0">
                    <span className="sr-only">Open menu</span>
                    <MoreHorizontal />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  onClick={(e) => e.stopPropagation()}
                >
                  {isOwner && matter.is_active && (
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate({
                          to: '/matter/$slug/members',
                          params: { slug: matter.guid },
                        });
                      }}
                    >
                      {t('invite-colleagues')}
                    </DropdownMenuItem>
                  )}

                  {isOwner && (
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleMatterStatus(e);
                      }}
                    >
                      {matter.is_active ? 'Archive' : 'Reactivate'}
                    </DropdownMenuItem>
                  )}

                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate({
                        to: '/matter/$slug/settings',
                        params: { slug: matter.guid },
                      });
                    }}
                  >
                    Settings
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        },
        size: 50,
        meta: {
          headerClassName: 'w-[80px] text-right',
          cellClassName: 'w-[80px] pr-4',
        },
      },
    ],
    [viewAllMatters, mutateMatters, mutateAllMatters, navigate, t],
  );

  const table = useReactTable<MatterAccess>({
    data: matters,
    columns: columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: (row, _columnId, filterValue) => {
      const query = String(filterValue ?? '').trim().toLowerCase();
      if (!query) return true;
      const matterNo = String(row.original.matter_no ?? '').toLowerCase();
      const name = String(row.original.name ?? '').toLowerCase();
      return matterNo.includes(query) || name.includes(query);
    },
    sortingFns: {
      ...sortingFns,
      myCustomSorting: sortingFns.alphanumeric,
    },
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      globalFilter,
    },
    columnResizeMode: 'onChange',
    defaultColumn: {
      minSize: 50,
    },
  });

  return (
    <div className="flex flex-col gap-4 px-6 pb-6 pt-4">
      <div className="flex flex-row gap-6">
        {activeTab === 'deadlines' ? (
          <DeadlinesPanel matterRegion={matterRegion} />
        ) : activeTab === 'conversations' ? (
          <ResumePanel />
        ) : activeTab === 'uploads' ? (
          <UploadsPanel />
        ) : (
          <Card flat className="h-full w-full px-6">
            <div className="flex items-center py-4">
              <div className="relative w-full max-w-sm pl-0.5">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder="Search matters by number or name..."
                  value={globalFilter}
                  onChange={(e) => setGlobalFilter(e.target.value)}
                  className="pl-9"
                />
              </div>
              <div className="ml-auto flex items-center gap-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline">
                      {viewAllMatters
                        ? 'Law firm matters'
                        : viewArchivedMatters
                          ? 'Archived matters'
                          : 'Active matters'}{' '}
                      <ChevronDown />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={() =>
                        navigate({
                          to: '/all-matters',
                          search: {
                            ...search,
                            archived: false,
                            viewAll: false,
                          },
                        })
                      }
                    >
                      Active matters
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() =>
                        navigate({
                          to: '/all-matters',
                          search: {
                            ...search,
                            archived: true,
                            viewAll: false,
                          },
                        })
                      }
                    >
                      Archived matters
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() =>
                        navigate({
                          to: '/all-matters',
                          search: { ...search, viewAll: true },
                        })
                      }
                    >
                      Law firm matters
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                {canCreateMatter && (
                  <Button
                    variant="default"
                    onClick={() => setCreateMatterDialogOpen(true)}
                  >
                    {t('create-team')}
                  </Button>
                )}
              </div>
            </div>
            <div className="mb-4 rounded-md border">
              <Table className="w-full table-fixed">
                <TableHeader>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <TableHead
                          key={header.id}
                          className={
                            (header.column.columnDef.meta as any)
                              ?.headerClassName
                          }
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                        </TableHead>
                      ))}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell
                        colSpan={columns.length}
                        className="h-24 text-center"
                      >
                        <div className="flex items-center justify-center">
                          <Loading />
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : isError ? (
                    <TableRow>
                      <TableCell
                        colSpan={columns.length}
                        className="h-24 text-center"
                      >
                        <div className="flex flex-col items-center gap-3">
                          <p className="text-sm text-muted-foreground">
                            Couldn&apos;t load matters.
                          </p>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => void retryLoad()}
                          >
                            Retry
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : table.getRowModel().rows.length ? (
                    table.getRowModel().rows.map((row) => (
                      <TableRow
                        key={row.id}
                        data-state={row.getIsSelected() && 'selected'}
                        onClick={
                          viewAllMatters
                            ? undefined
                            : () => handleRowMatterClick(row.original.guid)
                        }
                        className={viewAllMatters ? '' : 'cursor-pointer'}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <TableCell
                            key={cell.id}
                            className={
                              (cell.column.columnDef.meta as any)
                                ?.cellClassName
                            }
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={columns.length}
                        className="h-24 text-center"
                      >
                        No{' '}
                        {viewAllMatters
                          ? 'available'
                          : viewArchivedMatters
                            ? 'archived'
                            : 'active'}{' '}
                        matters found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            <div
              className={`m-2 flex items-center justify-between px-2 ${table.getSelectedRowModel().rows.length > 0 && 'mb-16'}`}
            >
              <div className="flex-1 text-sm text-muted-foreground">
                {table.getFilteredRowModel().rows.length}{' '}
                {viewAllMatters
                  ? 'available'
                  : viewArchivedMatters
                    ? 'archived'
                    : 'active'}{' '}
                matter
                {table.getFilteredRowModel().rows.length !== 1 && 's'}
              </div>
              {table.getPageCount() > 1 && (
                <div className="flex items-center space-x-6 lg:space-x-8">
                  <div className="flex w-[120px] items-center justify-center whitespace-nowrap text-sm font-medium">
                    Page {table.getState().pagination.pageIndex + 1} of{' '}
                    {table.getPageCount()}
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      className="hidden h-8 w-8 p-0 lg:flex"
                      onClick={() => table.setPageIndex(0)}
                      disabled={!table.getCanPreviousPage()}
                    >
                      <span className="sr-only">Go to first page</span>
                      <ChevronsLeftIcon className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      className="h-8 w-8 p-0"
                      onClick={() => table.previousPage()}
                      disabled={!table.getCanPreviousPage()}
                    >
                      <span className="sr-only">Go to previous page</span>
                      <ChevronLeftIcon className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      className="h-8 w-8 p-0"
                      onClick={() => table.nextPage()}
                      disabled={!table.getCanNextPage()}
                    >
                      <span className="sr-only">Go to next page</span>
                      <ChevronRightIcon className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      className="hidden h-8 w-8 p-0 lg:flex"
                      onClick={() =>
                        table.setPageIndex(table.getPageCount() - 1)
                      }
                      disabled={!table.getCanNextPage()}
                    >
                      <span className="sr-only">Go to last page</span>
                      <ChevronsRightIcon className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </Card>
        )}
      </div>
      <CreateMatterDialog
        isOpen={createMatterDialogOpen}
        onClose={() => setCreateMatterDialogOpen(false)}
      />
    </div>
  );
}
